import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Calendar } from 'lucide-react';
import { useVacationRequests } from '../../hooks/useVacationRequests';
import { useHolidays } from '../../hooks/useHolidays';
import { toast } from 'sonner';
import { chileanInputDateToISO } from '../../utils/dateUtils';

export const VacationRequestDialog = ({ employee, open, onOpenChange, onRequestCreated }) => {
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    type: 'full',
    notes: '',
    manualHolidays: 0,
  });
  const [calculatedDays, setCalculatedDays] = useState(0);
  
  const { createRequest } = useVacationRequests();
  const { calculateWorkingDays } = useHolidays();

  // Calcular días cuando cambien las fechas
  useEffect(() => {
    const calcDays = async () => {
      if (formData.startDate && formData.endDate) {
        try {
          const response = await calculateWorkingDays(formData.startDate, formData.endDate);
          const workingDays = response.success ? response.data.workingDays : 0;
          const manual = Number.isFinite(Number(formData.manualHolidays)) ? Math.max(0, Math.floor(Number(formData.manualHolidays))) : 0;
          const base = Math.max(0, workingDays - manual);
          setCalculatedDays(formData.type === 'half' ? base * 0.5 : base);
        } catch (error) {
          console.error('Error calculating working days:', error);
          setCalculatedDays(0);
        }
      } else {
        setCalculatedDays(0);
      }
    };
    
    if (formData.startDate && formData.endDate) {
      calcDays();
    }
  }, [formData.startDate, formData.endDate, formData.type, formData.manualHolidays]); // recalcula si cambian festivos manuales

  // Si no hay empleado, no renderizar el contenido
  if (!employee) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
          </DialogHeader>
          <p>No se ha seleccionado un empleado válido.</p>
        </DialogContent>
      </Dialog>
    );
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!employee) {
      toast.error('No se ha seleccionado un empleado válido');
      return;
    }
    
    if (!formData.startDate || !formData.endDate) {
      toast.error('Por favor seleccione las fechas');
      return;
    }
    
    if (new Date(chileanInputDateToISO(formData.startDate)) > new Date(chileanInputDateToISO(formData.endDate))) {
      toast.error('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }
    
    const availableDays = formData.type === 'administrative' 
      ? employee.admin_days_available 
      : employee.vacation_days_available;
    
    if (calculatedDays > availableDays) {
      toast.error('No tiene días suficientes disponibles');
      return;
    }
    
    try {
      console.log('Enviando solicitud de vacaciones...');
      const requestData = {
        employeeId: employee.id || employee._id,
        startDate: chileanInputDateToISO(formData.startDate),
        endDate: chileanInputDateToISO(formData.endDate),
        type: formData.type, // 'full', 'half' o 'administrative'
        totalDays: calculatedDays,
        notes: formData.notes,
        manualHolidays: Number.isFinite(Number(formData.manualHolidays)) ? Math.max(0, Math.floor(Number(formData.manualHolidays))) : 0,
      };
      
      console.log('Datos de solicitud:', requestData);
      
      const response = await createRequest(requestData);
      console.log('Respuesta de createRequest:', response);
      
      toast.success('Solicitud de vacaciones enviada exitosamente');
      onRequestCreated?.(response.data);
      onOpenChange(false);
      
      // Limpiar formulario
      setFormData({
        startDate: '',
        endDate: '',
        type: 'full',
        notes: '',
        manualHolidays: 0,
      });
      setCalculatedDays(0);
    } catch (error) {
      console.error('Error creating vacation request:', error);
      toast.error(error.message || 'Error al enviar la solicitud de vacaciones');
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Solicitar Vacaciones - {employee.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Fecha Inicio *</Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="endDate">Fecha Fin *</Label>
              <Input
                id="endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="type">Tipo de Vacaciones</Label>
            <Select value={formData.type} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Días Completos</SelectItem>
                <SelectItem value="half">Medios Días</SelectItem>
                <SelectItem value="administrative">Días Administrativos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {calculatedDays > 0 && (
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-sm font-medium">
                Días calculados: <span className="text-primary">{calculatedDays} días</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                (Solo días hábiles, excluyendo fines de semana y festivos)
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="manualHolidays">Festivos manuales a descontar (opcional)</Label>
              <Input
                id="manualHolidays"
                type="number"
                min="0"
                step="1"
                value={formData.manualHolidays}
                onChange={(e) => setFormData(prev => ({ ...prev, manualHolidays: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Si conoces festivos en el período y aún no están cargados, ingrésalos aquí para restarlos.</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Observaciones</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Motivo o comentarios adicionales..."
              rows={3}
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={calculatedDays === 0}>
              Crear Solicitud
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
